import { doc, getDoc, setDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { db } from './firebase';

// The display name is denormalized in two places besides the authoritative
// users/{uid}.name: publicUsers/{uid}.name (readable by other users) and
// authorName on every recipe the user has posted. Renaming must update both,
// or other people keep seeing the old name on the user's recipes.

/**
 * Propagate [name] to the user's public profile + all their recipe posts.
 * Idempotent and safe to call repeatedly (skips posts already up to date).
 */
export async function propagateUserName(uid, name) {
    if (!uid || !name) return;
    await setDoc(doc(db, 'publicUsers', uid), { name }, { merge: true });
    const postsSnap = await getDocs(collection(db, 'users', uid, 'posts'));
    const stale = postsSnap.docs.filter((d) => d.data().authorName !== name);
    // writeBatch caps at 500 ops — chunk to stay under it.
    for (let i = 0; i < stale.length; i += 450) {
        const batch = writeBatch(db);
        stale.slice(i, i + 450).forEach((d) => batch.update(d.ref, { authorName: name }));
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
    }
}

/**
 * Self-heal: if the public mirror is out of sync with the authoritative
 * users.name (e.g. the account was renamed before this propagation existed),
 * push the current name out to the denormalized copies. Call once on login.
 */
export async function healUserName(uid, currentName) {
    if (!uid || !currentName) return;
    try {
        const pub = await getDoc(doc(db, 'publicUsers', uid));
        if (pub.exists() && pub.data().name === currentName) return;
        await propagateUserName(uid, currentName);
    } catch (e) {
        // best-effort — never block login on this
    }
}
